import { useState } from "react";
import { Container, Col, Row, FormGroup, Label, Input } from "reactstrap";
import { Component as Form } from "@repoflow/form-schema";
import { BreadcrumbItem } from "reactstrap";
import { useHistory } from "react-router-dom";
import { useMutation } from "@apollo/client";
import * as Notify from "@repoflow/form-schema/notify";
import * as OrganizationKeysQL from "queries/organization-key";
import { CancellableContent } from "@repoflow/components/content-cancel";
import {
  Mutation,
  Button,
  useSectionContext,
} from "@repoflow/components/section";
import * as tags from "@repoflow/form-schema/widgets/tag";
import * as KeyAccess from "components/key-access";
import { Button as PlainButton, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';

export const Sidebar = () => null;

export const Indice = () => <BreadcrumbItem>New</BreadcrumbItem>;

// The secret, once: auth stores its hash, so closing this is the last anybody
// sees of it.
const SecretModal = ({ secret, onClose }) => (
  <Modal isOpen={secret !== null} toggle={onClose}>
    <ModalHeader toggle={onClose}>API Key Secret</ModalHeader>
    <ModalBody>
      Copy and save your API key secret: <br />
      <b data-testid="key-secret">{secret}</b>
      <br />
      You are not going to see this secret again.
    </ModalBody>
    <ModalFooter>
      <PlainButton color="primary" onClick={onClose}>Close</PlainButton>
    </ModalFooter>
  </Modal>
);

// The Kind select's value for an agent key; "" is an organization-wide key.
export const AGENT = "agent";

/**
 * Which key to mint: organization-wide, as this form always did, or an agent
 * key bound to one resource.
 */
export const KindChoice = ({ value, onChange }) => (
  <FormGroup>
    <Label for="key-kind">Kind</Label>
    <Input type="select" id="key-kind" value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">Organization-wide key</option>
      <option value={AGENT}>Agent key</option>
    </Input>
  </FormGroup>
);

const TextField = ({ id, label, value, onChange, placeholder }) => (
  <FormGroup>
    <Label for={id}>{label}</Label>
    <Input id={id} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
  </FormGroup>
);

/**
 * An agent key: one resource, named by the service that owns it, its type and
 * its id — typed in, because auth-repoflow knows no service's resources. auth
 * checks only their shape; whether the resource exists and is this
 * organization's is answered by that service whenever the key is used, so the
 * names are the user's to get right (docs/scoped-keys.md §The scope). The rate
 * limit is the default one; nothing but a hand edit sets another.
 */
export const AgentKeyForm = ({ params, onCreated }) => {
  const [service, setService] = useState("");
  const [type, setType] = useState("");
  const [id, setId] = useState("");
  const [description, setDescription] = useState("");
  const input = KeyAccess.agentInput({ service, type, id, description });

  const [create, { loading: creating }] = useMutation(OrganizationKeysQL.create, {
    update: OrganizationKeysQL.update(params),
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!input) {
      return;
    }
    try {
      const result = await create({ variables: { ...params, input } });
      onCreated(result.data.viewer.account.domains.get.organizations.get.keys.create.keySecret);
    } catch (failure) {
      Notify.error(KeyAccess.describeCreateError(failure));
    }
  };

  return (
    <form aria-label="Agent key" onSubmit={submit}>
      <TextField id="agent-key-service" label="Service" value={service} onChange={setService} />
      <TextField id="agent-key-type" label="Type" value={type} onChange={setType} />
      <TextField id="agent-key-id" label="Resource id" value={id} onChange={setId} />
      <TextField
        id="agent-key-description"
        label="Who is it for?"
        value={description}
        onChange={setDescription}
        placeholder="Optional"
      />
      <p className="small text-muted">
        Use the service, type and id the service you are granting access to gives you. They are not
        checked here: a key naming a resource that is not this organization's reaches nothing. An
        agent key has the <code>agent</code> role only, reaches that one resource and nothing else,
        and is held to 60 checks a minute and 5,000 a day.
      </p>
      <PlainButton type="submit" color="success" disabled={!input || creating}>
        {creating ? "Creating…" : "Create"}
      </PlainButton>
    </form>
  );
};

export const Content = ({
  match: {
    params,
    params: { domain, orgId },
  },
}) => {
  const [phase, setPhase] = useState("init");
  const [secret, setSecret] = useState(null);
  // "" is an organization-wide key; AGENT an agent key.
  const [kind, setKind] = useState("");
  // The view this key was created from.
  const {
    parent: { base },
  } = useSectionContext();
  const history = useHistory();

  const closeSecret = () => {
    setSecret(null);
    history.replace(base);
  };

  const initial = {
    roles: [],
  };
  return (
    <CancellableContent>
      <SecretModal secret={secret} onClose={closeSecret} />
      <Container className="mt-4">
        <Row>
          <Col>
            <KindChoice value={kind} onChange={setKind} />
          </Col>
        </Row>
        <Row>
          <Col>
            {kind ? (
              <AgentKeyForm params={{ domain, orgId }} onCreated={setSecret} />
            ) : (
              <Mutation
                initial={initial}
                context={{
                  phase,
                }}
                target={{ $ref: "/Public/Key/Create" }}
                mutation={OrganizationKeysQL.create}
                update={OrganizationKeysQL.update(params)}
                onMutate={({ state: { values }, handler }) => {
                  setPhase("loading");

                  // The fields the form shows and nothing else: /Public/Key/Create
                  // also lists `scope`, which only AgentKeyForm above sends. See
                  // components/key-access createInput.
                  handler({
                    variables: {
                      domain,
                      orgId,
                      input: KeyAccess.createInput(values, ["roles"]),
                    },
                  });
                }}
                onCompleted={(data) => {
                  const keySecret = data.viewer.account.domains.get.organizations.get.keys.create.keySecret;
                  setSecret(keySecret);
                }}
                onError={(error) => {
                  // Without this the Create button keeps spinning, disabled, forever.
                  setPhase("init");
                  Notify.error(error.toString());
                }}
              >
                {MutationHandler}
              </Mutation>
            )}
          </Col>
        </Row>
      </Container>
    </CancellableContent>
  );
};

const MutationHandler = ({
  schema,
  state,
  mutate,
  context: {
    phase,
  },
  target,
}) => {
  const { values, setValues } = state;

  const formProps = {
    schema,
    target,
    onSubmit: () => {
      mutate();
    },
    onChange: (vals) => {
      setValues(vals);
    },
    config: {
      "/Public/Key/Create": {
        fields: [
          "roles"
        ],
      },
    },
    values,
    widgets: { tags }
  };

  return (
    <Form {...formProps}>
      <Button type="submit" loading={phase === "loading"}>
        Create
      </Button>
    </Form>
  );
};
